import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AvailableLocale } from '@/i18n'
import { signOutAction } from '../actions'

export type KanbanLayout = 'horizontal' | 'vertical'

/**
 * Which of the signed-in user's work is shown on a kanban board:
 * - `all` — no assignment filtering; every work order is shown.
 * - `reportableNow` — only work orders that have a job assigned to the user on a
 *   column they can report time on right now; the order is shown only in those
 *   columns.
 * - `assignedToMe` — every work order the user is assigned to on any job, shown
 *   across all of its columns regardless of reportability.
 */
export type AssignedJobsFilter = 'all' | 'reportableNow' | 'assignedToMe'

/**
 * Card order within each kanban column:
 * - `lastMoved` — the API order, most recently moved first.
 * - `arrivalDate` / `deliveryDate` — earliest date first, cards without the date last.
 */
export type KanbanSortOrder = 'lastMoved' | 'arrivalDate' | 'deliveryDate'

export type KanbanFilters = {
  whitelistedWorkflowStates: string[]
  assignedJobsFilter: AssignedJobsFilter
  sortOrder: KanbanSortOrder
}

export type SettingsState = {
  isDeveloper: boolean
  locale: AvailableLocale | 'auto'
  activeWorkshopId?: string
  isAppSidebarOpen: boolean
  minimizeEmptyColumns: boolean
  kanbanLayouts: {
    planning: KanbanLayout
    workshop: KanbanLayout
    handoff: KanbanLayout
  }
  kanbanFilters: {
    planning: KanbanFilters
    workshop: KanbanFilters
    handoff: KanbanFilters
  }
}

export const initialState: SettingsState = {
  isDeveloper: false,
  locale: 'auto',
  activeWorkshopId: undefined,
  isAppSidebarOpen: true,
  minimizeEmptyColumns: false,
  kanbanLayouts: {
    planning: 'vertical',
    workshop: 'horizontal',
    handoff: 'vertical',
  },
  kanbanFilters: {
    planning: {
      whitelistedWorkflowStates: [],
      assignedJobsFilter: 'all',
      sortOrder: 'lastMoved',
    },
    workshop: {
      whitelistedWorkflowStates: [],
      assignedJobsFilter: 'all',
      sortOrder: 'lastMoved',
    },
    handoff: {
      whitelistedWorkflowStates: [],
      assignedJobsFilter: 'all',
      sortOrder: 'lastMoved',
    },
  },
}

const SettingsSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(signOutAction, state => {
      state.activeWorkshopId = undefined
    })
  },
  initialState,
  name: 'settings',
  reducers: {
    setIsDeveloper(state, action: PayloadAction<boolean>) {
      state.isDeveloper = action.payload
    },
    setLocale(state, action: PayloadAction<AvailableLocale | 'auto'>) {
      state.locale = action.payload
    },
    setActiveWorkshopId(state, action: PayloadAction<string | undefined>) {
      state.activeWorkshopId = action.payload
    },
    setIsAppSidebarOpen(state, action: PayloadAction<boolean>) {
      state.isAppSidebarOpen = action.payload
    },
    setMinimizeEmptyColumns(state, action: PayloadAction<boolean>) {
      state.minimizeEmptyColumns = action.payload
    },
    setKanbanLayout(
      state,
      action: PayloadAction<{ page: keyof SettingsState['kanbanLayouts']; layout: KanbanLayout }>,
    ) {
      state.kanbanLayouts[action.payload.page] = action.payload.layout
    },
    setKanbanFilters(
      state,
      action: PayloadAction<{ page: keyof SettingsState['kanbanFilters']; filters: KanbanFilters }>,
    ) {
      state.kanbanFilters[action.payload.page] = action.payload.filters
    },
  },
})

export const SettingsReducer = SettingsSlice.reducer

export const SettingsActions = SettingsSlice.actions
